import React from 'react';
import {
  CircleDollarSign,
  Mail,
  NotebookPen,
  Reply,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';

type ThumbTheme =
  | 'prospection'
  | 'marques'
  | 'tarifs'
  | 'droits'
  | 'relances'
  | 'email'
  | 'collaborations'
  | 'ugc'
  | 'default';

const THEME_PRIORITY: Array<{ tag: string; theme: ThumbTheme }> = [
  { tag: 'tarifs', theme: 'tarifs' },
  { tag: 'relance', theme: 'relances' },
  { tag: 'relances', theme: 'relances' },
  { tag: 'email', theme: 'email' },
  { tag: 'emails', theme: 'email' },
  { tag: 'droits', theme: 'droits' },
  { tag: 'marques', theme: 'marques' },
  { tag: 'prospection', theme: 'prospection' },
  { tag: 'collaborations', theme: 'collaborations' },
  { tag: 'ugc', theme: 'ugc' },
];

const THEME_ICON: Record<ThumbTheme, React.ComponentType<{ className?: string }>> = {
  prospection: Target,
  marques: NotebookPen,
  tarifs: CircleDollarSign,
  droits: ShieldCheck,
  relances: Reply,
  email: Mail,
  collaborations: Users,
  ugc: Sparkles,
  default: Sparkles,
};

export function resolveBlogThumbTheme(tags: string[]): ThumbTheme {
  const normalized = new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean));
  for (const { tag, theme } of THEME_PRIORITY) {
    if (normalized.has(tag)) return theme;
  }
  return 'default';
}

function LeadsmeMark({ className = '' }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className={className} aria-hidden>
      <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="16" cy="16" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="16" cy="16" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="16" cy="16" r="2" fill="currentColor" />
    </svg>
  );
}

interface BlogThumbProps {
  tags?: string[];
  className?: string;
  /** Compact square, or full-height side rail for horizontal cards */
  size?: 'sm' | 'md' | 'side';
  /** Featured / brand rail: Leadsme mark on blue */
  brand?: boolean;
}

const BlogThumb: React.FC<BlogThumbProps> = ({
  tags = [],
  className = '',
  size = 'md',
  brand = false,
}) => {
  const Icon = brand ? null : THEME_ICON[resolveBlogThumbTheme(tags)];

  const isSide = size === 'side';
  const box = isSide
    ? `h-auto min-h-full self-stretch ${className || 'w-14 sm:w-[4.75rem] md:w-[5.25rem]'}`
    : `${size === 'sm' ? 'w-24 h-24 sm:w-28 sm:h-28' : 'w-28 h-28 sm:w-32 sm:h-32'} ${className}`;
  const iconSize = isSide
    ? 'h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9'
    : size === 'sm'
      ? 'h-9 w-9 sm:h-10 sm:w-10'
      : 'h-11 w-11';
  const radius = isSide ? 'rounded-none' : 'rounded-2xl';

  return (
    <div
      className={`relative shrink-0 overflow-hidden bg-blue-50 ${box} ${radius} ${isSide ? '' : 'ring-1 ring-black/[0.03]'}`}
      aria-hidden
    >
      <div className="absolute inset-0 flex items-center justify-center">
        {brand ? (
          <LeadsmeMark className={`${iconSize} text-blue-600`} />
        ) : (
          Icon && <Icon className={`${iconSize} text-blue-600 stroke-[1.6]`} />
        )}
      </div>
    </div>
  );
};

export default BlogThumb;
